import clsx from 'clsx';
import parseHtmlToReact from 'html-react-parser';
import { memo, useMemo } from 'react';

import type { RelevancyCardProps } from '../../DynamicCardComponent';
import { useRelevancyOptions } from '../../store';
import BaseCardComponent from '../BaseCardComponent';

import { bolderIsNumber, disableLink, isDescriptionLink } from './index.controller';

import { trackCardElement } from '@services/GoogleTagManagerService';
import { getArticleUrl } from '@services/LinkRuleService';
import { useWindowSize } from '@shared/hooks/useWindowSize';

import * as styles from './styles.module.scss';

export const MessageComponent = (props: RelevancyCardProps) => {
  const {
    item: { teaser },
  } = props;

  const { options } = useRelevancyOptions();
  const { isTablet, isMobile } = useWindowSize();

  const trackingData = useMemo(
    () => ({
      name: teaser.title,
      id: teaser.id,
      category: options.trackingCategory ?? 'message card',
    }),
    [teaser.title, teaser.id, options.trackingCategory],
  );

  const _disableLink = useMemo(() => disableLink(teaser), [teaser]);

  const link = useMemo(() => teaser.targetUrl ?? getArticleUrl(teaser.id) ?? teaser.webReference, [teaser]);
  const url = useMemo(() => (isDescriptionLink(teaser) ? teaser?.description : link), [link, teaser]);
  const buttonText = useMemo(
    () => (_disableLink ? undefined : teaser.ctaLabel ? teaser.ctaLabel : undefined),
    [_disableLink, teaser.ctaLabel],
  );
  const buttonTag = useMemo(
    () => (_disableLink ? undefined : teaser.ctaLabel ? teaser.ctaLabel : 'learn_more'),
    [_disableLink, teaser.ctaLabel],
  );
  const hugeText = useMemo(() => bolderIsNumber(teaser), [teaser]);

  const titleJsx = useMemo(() => {
    return <>{parseHtmlToReact(teaser.headline)}</>;
  }, [teaser.headline]);

  return (
    <div
      data-testid='message-component-root'
      className={clsx('card', hugeText && styles.huge, styles.editorialInfo)}
      ref={trackCardElement(trackingData)}
    >
      <BaseCardComponent
        {...props}
        title={teaser.title}
        titleJsx={titleJsx}
        isVideoPlayer={teaser?.type === 'videostory'}
        video={teaser?.type === 'videostory' ? teaser : undefined}
        buttonText={buttonText}
        buttonTag={buttonTag}
        url={url}
        disableLink={_disableLink}
        image={teaser.mainMedia?.image}
        showRoofline={options.showRoofline}
        buttonAppearance={isMobile || isTablet ? 'text' : 'outlined'}
        highContrast
      />
    </div>
  );
};

export default memo(MessageComponent);
